// ============================================================================
// Palette de la feuille de brouillon : les bouts proposés à l'élève.
// ============================================================================
// Dérivée du modèle, jamais stockée : le prototype les écrivait à la main
// (énoncé tapable, `pool`, `ops`) ; dans l'app, on les recalcule à partir de
// ce que le modèle sait déjà.
//
//   statement : les grandeurs (AB, x…) et les nombres de l'énoncé (égalités
//               affichées, valeurs données, formules des SKILLCARDS) ;
//   tools     : les grandeurs de la SKILLCARD (`tool`) : V₀, V₁, k… — rangées
//               à part, dans un groupe replié ;
//   answers   : les réponses proposées par le QCM (valeurs) ;
//   values    : les nombres et monômes du corrigé du moteur ET des pièges,
//               triés (jamais dans l'ordre du corrigé : ils ne le dévoilent pas) ;
//   ops       : les opérations utiles à cette question.
//
// Pur : aucun DOM. La feuille (apps/app, features/scratch-sheet) l'affiche.
// ============================================================================

import { formatNumber, isNum, isPow, isSupPow, isVar, lex, normalize, supPowValue } from './lexer';
import { kernelSpecialPlugin, usesKernelPoly, type ScratchModel } from './model';
import { solve, type SolutionStep } from './reasoner';
import { atomsText, parseExpr, roundTo } from './tree';
import { isAlgebraic } from './complex';
import { isUnitAtom, numbersIn, unitAtom, unitsIn } from './units';
import { GEO_RELATIONS, geoChipsOf } from './geometry';
import { GEO_RELATIONS as KGEO_RELATIONS, looksGeometric, gpropChips } from './kernel/geometry';
import { kprogCodePaletteParts, kprogLineAtoms, kprogPaletteParts } from './kernel-python';
import { kmatLineAtoms, kmatPaletteParts } from './kernel-matrix';
import { karithLineAtoms, karithPaletteParts } from './kernel-arith';
import { klimLineAtoms, klimPaletteParts } from './kernel-limit';
import { klcPaletteParts } from './kernel-limit-claims';
import { kintLineAtoms, kintPaletteParts } from './kernel-integral';
import { kodeLineAtoms, kodePaletteParts, odeVariable } from './kernel-ode';
import { kpartLineAtoms, kpartPaletteParts, partialSpecOf } from './kernel-partial';
import { kriemLineAtoms, kriemPaletteParts } from './kernel-riemann';
import { kclaimsPaletteParts } from './kernel-claims';
import { kanaLineAtoms, kanaPaletteParts } from './kernel-analysis';
import { analysisModelOf } from './kernel-variations';
import { isGridText, ktabPaletteParts } from './kernel-tables';
import { kforLineAtoms, kforPaletteParts } from './kernel-formula';
import { isEntryAtom, matText } from './kernel/matrix';

export type ChipKind = 'num' | 'var' | 'expr' | 'op' | 'word';

export interface PaletteChip {
  atoms: string[];
  /** texte affiché (« / » pour la division) */
  text: string;
  kind: ChipKind;
}

export interface Palette {
  statement: PaletteChip[];
  /** grandeurs de la SKILLCARD (`symbols[n].tool`) : groupe replié du panneau */
  tools?: PaletteChip[];
  answers: PaletteChip[];
  values: PaletteChip[];
  /** opérations, dans l'ordre d'affichage */
  ops: string[];
}

/** les opérations possibles, dans l'ordre d'affichage (« = » en dernier) */
export const OP_ORDER = [
  '+',
  '−',
  '×',
  '÷',
  '(',
  ')',
  '²',
  '^',
  // moteur v6 : factorielle (« 5! »)
  '!',
  // moteur v27 : « k parmi n » (coefficient binomial)
  'parmi',
  '√',
  // moteur v2 : fonctions usuelles et π (proposées seulement si l'exercice s'en sert)
  'ln',
  'exp',
  'cos',
  'sin',
  'tan',
  // moteur v17 : réciproques (en degrés)
  'arccos',
  'arcsin',
  'arctan',
  // moteur v45 : logarithme décimal
  'log',
  // moteur v11 : module et argument d'un complexe
  'arg',
  '|',
  'π',
  '°',
  '%',
  'ou',
  // moteur v5 : comparaisons (« Δ > 0 »)
  '>',
  '<',
  '≥',
  '≤',
  // moteur v10 : valeur arrondie (« AC ≈ 8,4 »)
  '≈',
  // moteur v3 : congruence (le modulo « [7] » de la question suit « = »)
  '≡',
  '=',
] as const;
const FUNC_OPS = new Set([
  'ln',
  'exp',
  'cos',
  'sin',
  'tan',
  'arg',
  'arccos',
  'arcsin',
  'arctan',
  'log',
]);
const INEQ_OPS = ['>', '<', '≥', '≤'] as const;
const OP_WORDS: Record<string, true> = { ou: true, parmi: true };

/** Nature d'un bout : nombre, lettre, expression, opération. */
export function chipKind(atoms: string[]): ChipKind {
  if (atoms.length === 1) {
    const a = atoms[0]!;
    if (isNum(a)) return 'num';
    if (isUnitAtom(a)) return 'var';
    // géométrie (moteur v4) : une droite « (AB) », un segment « [AB] »
    if (/^[([][A-Za-zΔ][^()[\]]*[)\]]$/.test(a)) return 'var';
    // arithmétique (moteur v35) : un PGCD écrit « PGCD(1071 ; 462) »
    if (/^PGCD\(/.test(a)) return 'var';
    if (isPow(a)) return /ⁿ/.test(a) ? 'expr' : 'num';
    if (isVar(a)) return 'var';
    return OP_WORDS[a] ? 'word' : 'op';
  }
  return atoms.some((a) => isVar(a) || (isPow(a) && /ⁿ/.test(a))) ? 'expr' : 'num';
}

/** Clé d'un bout : deux bouts de même clé s'écrivent pareil (3x = 3 × x). */
export function chipKey(atoms: string[]): string {
  return normalize(atoms).join(' ');
}

function chip(atoms: string[]): PaletteChip {
  return { atoms, text: atomsText(atoms), kind: chipKind(atoms) };
}

/**
 * Découpe une suite d'atomes en bouts, comme l'élève les voit : « 3x », « x² »,
 * « 2x² » forment un seul bout ; les nombres, lettres et opérations sont seuls.
 */
export function groupAtoms(atoms: string[]): PaletteChip[] {
  const out: PaletteChip[] = [];
  for (let i = 0; i < atoms.length; i++) {
    const a = [atoms[i]!];
    if (isNum(atoms[i]) && isVar(atoms[i + 1])) a.push(atoms[++i]!);
    if ((isVar(atoms[i]) || isNum(atoms[i])) && atoms[i + 1] === '²') a.push(atoms[++i]!);
    out.push(chip(a));
  }
  return out;
}

/** Une ligne de texte découpée en bouts (recopie d'une ligne de l'énoncé). */
export function chipsFromText(text: string): PaletteChip[] {
  return groupAtoms(lex(text));
}

/** Les termes d'une ligne qui méritent un bout : nombres, puissances, monômes (3x, 2x²). */
function termsOf(atoms: string[], symbols: Record<string, unknown>): PaletteChip[] {
  const out: PaletteChip[] = [];
  groupAtoms(atoms).forEach((c) => {
    const a = c.atoms;
    if (a.length === 1) {
      if (isNum(a[0]) || isPow(a[0])) out.push(c);
      else if (isVar(a[0]) && !symbols[a[0]!]) out.push(c); // lettre d'un outil (n…)
      return;
    }
    // 5² : le nombre seul (le carré vient de l'opération « ² ») ; x² : la lettre suffit
    if (a.length === 2 && a[1] === '²') {
      if (isNum(a[0])) out.push(chip([a[0]!]));
      return;
    }
    // monômes : 3x, 2x² (1x s'écrit x : pas de bout)
    if (isNum(a[0]) && isVar(a[1]) && a[0] !== '1') {
      out.push(c);
      out.push(chip([a[0]!]));
    }
  });
  return out;
}

function numberValue(c: PaletteChip): number | null {
  if (c.atoms.length !== 1 || !isNum(c.atoms[0])) return null;
  return Number(c.atoms[0]!.replace(',', '.'));
}

/** Les nombres d'abord, du plus petit au plus grand ; puis les expressions. */
function byValue(a: PaletteChip, b: PaletteChip): number {
  const va = numberValue(a);
  const vb = numberValue(b);
  if (va !== null && vb !== null) return va - vb;
  if (va !== null) return -1;
  if (vb !== null) return 1;
  return a.text.localeCompare(b.text, 'fr');
}

/**
 * Les bouts de la feuille, dérivés du modèle. `solution` : le corrigé du
 * moteur s'il est déjà calculé (sinon il est recalculé ici).
 */
export function derivePalette(model: ScratchModel, solution?: SolutionStep[]): Palette {
  // mode spécial passé au noyau (moteur v34) : la palette du mode, sans les valeurs des
  // pièges ni des choix du QCM (D6 : énoncé + corrigé)
  if (kernelSpecialPlugin(model)) {
    const { kernel: _k, traps: _t, choices: _c, ...legacy } = model;
    return derivePalette(legacy as ScratchModel, solution);
  }
  if (model.kernel?.plugin === 'geometry') return kernelGeometryPalette(model);
  if (model.kernel?.plugin === 'python') return pythonPalette(model);
  if (model.kernel?.plugin === 'matrix') return matrixPalette(model);
  if (model.kernel?.plugin === 'arith') return arithPalette(model);
  if (model.kernel?.plugin === 'limit') return limitPalette(model);
  if (model.kernel?.plugin === 'limclaims') return limitClaimsPalette(model);
  if (model.kernel?.plugin === 'integral') return integralPalette(model);
  if (model.kernel?.plugin === 'ode') return odePalette(model);
  if (model.kernel?.plugin === 'partial') return partialPalette(model);
  if (model.kernel?.plugin === 'riemann') return riemannPalette(model);
  if (model.kernel?.plugin === 'claims') return claimsPalette(model);
  if (model.kernel?.plugin === 'analysis') return analysisPalette(model);
  if (model.kernel?.plugin === 'variations') return variationsPalette(model);
  if (model.kernel?.plugin === 'tables') return tablesPalette(model);
  if (model.kernel?.plugin === 'formula') return formulaPalette(model);
  if (model.units) return unitsPalette(model);
  if (model.geometry) return geometryPalette(model);
  if (model.bounds) return boundsPalette(model);
  const symbols = model.symbols;
  const taken = new Set<string>();
  const take = (list: PaletteChip[], c: PaletteChip) => {
    const k = chipKey(c.atoms);
    if (!c.atoms.length || taken.has(k)) return;
    taken.add(k);
    list.push(c);
  };
  // toutes les lignes connues : elles décident aussi des opérations utiles
  const lines: string[][] = [];

  // ── énoncé : les grandeurs, puis les nombres donnés ──
  const statement: PaletteChip[] = [];
  const tools: PaletteChip[] = [];
  const cong = model.congruence;
  // congruences : seules les lettres de la question (pas une grandeur interne « reste »)
  const congText = cong ? [cong.target, ...(model.givens ?? [])].join(' ') : '';
  // nombres complexes (moteur v11) : seules les lettres de la question
  const cplx = model.complex;
  const cplxText = cplx
    ? [cplx.target, ...(model.givens ?? []), ...Object.keys(cplx.defs ?? {})].join(' ')
    : '';
  Object.keys(symbols).forEach((n) => {
    if (cong && !lex(congText).includes(n)) return;
    if (cplx && !lex(cplxText).includes(n)) return;
    take(symbols[n]?.tool ? tools : statement, chip([n]));
  });
  if (cplx) {
    const ta = lex(cplx.target);
    lines.push(ta);
    // le bout « i », puis les complexes de la question entre parenthèses : « (1 + i) »
    take(statement, chip(['i']));
    const stack: number[] = [];
    ta.forEach((a, k) => {
      if (a === '(') stack.push(k);
      if (a === ')' && stack.length) {
        const from = stack.pop()!;
        const g = ta.slice(from, k + 1);
        if (g.includes('i') && !g.slice(1, -1).includes('(') && algebraicAtoms(g.slice(1, -1)))
          take(statement, chip(g));
      }
    });
    groupAtoms(ta).forEach((c) => {
      if (c.atoms.length === 1 && (isNum(c.atoms[0]) || isPow(c.atoms[0]))) take(statement, c);
    });
  }
  if (cong) {
    lines.push(lex(cong.target));
    groupAtoms(lex(cong.target)).forEach((c) => {
      if (c.atoms.length === 1 && (isNum(c.atoms[0]) || isPow(c.atoms[0]))) take(statement, c);
    });
  }
  const statementLines = [
    // feuille du noyau (moteur v18) : la ligne de départ s'affiche en tête ;
    // un système (moteur v22) : toutes ses équations
    ...(model.kernel ? (model.kernel.system?.equations ?? [model.kernel.start]) : []),
    // récurrence (moteur v26) : l'hypothèse de récurrence, en tête de feuille elle aussi
    ...(model.kernel?.induction ? [model.kernel.induction.hypothesis] : []),
    ...(model.givens ?? []),
    ...(model.admitted ?? []),
    ...(model.preds ?? []).flatMap((p) => (p.skill === 'formule' ? [p.line] : [])),
  ].map((t) => lex(t));
  // comparaisons décisives (moteur v5) : elles décident des opérations « > », « < »…
  (model.checks ?? []).forEach((c) => lines.push(lex(c.line)));
  lines.push(...statementLines);
  Object.values(symbols).forEach((s) => {
    // valeur exacte (moteur v2) : le bout s'écrit « √2 ÷ 2 », jamais un arrondi
    if (s.given && s.exact?.length) take(statement, chip(lex(s.exact[0]!)));
    else if (s.given && s.value !== undefined) take(statement, chip(lex(formatNumber(s.value))));
  });
  (model.preds ?? []).forEach((p) => {
    if (p.skill === 'baisse') {
      if (typeof p.base === 'number') take(statement, chip(lex(formatNumber(p.base))));
      take(statement, chip(lex(formatNumber(p.taux))));
      lines.push(['%']);
    }
  });
  statementLines.forEach((atoms) => termsOf(atoms, symbols).forEach((c) => take(statement, c)));

  // ── réponses proposées (valeurs) ──
  // feuille du noyau (D6 du lot 6) : ni les valeurs des choix du QCM ni celles des pièges
  // (énoncé + corrigé : peu de bouts, et la réponse en fait partie)
  const qcm = model.kernel ? [] : (model.choices ?? []);
  const answers: PaletteChip[] = [];
  qcm.forEach((c) => {
    if (c.exact) take(answers, chip(lex(c.exact)));
    else if (c.value !== undefined) take(answers, chip(lex(formatNumber(c.value))));
    if (c.line) lines.push(lex(c.line));
  });
  answers.sort(byValue);

  // ── valeurs : corrigé du moteur + pièges, triées ──
  let path = solution;
  if (!path) {
    try {
      path = solve(model).path;
    } catch {
      path = [];
    }
  }
  const pool: PaletteChip[] = [];
  path.forEach((s) => lines.push(s.atoms));
  (model.kernel ? [] : (model.traps ?? [])).forEach((t) => lines.push(lex(t.line)));
  qcm.forEach((c) => {
    if (c.line) lines.push(lex(c.line));
  });
  (model.answers ?? []).forEach((a) => lines.push(lex(a)));
  lines.forEach((atoms) => termsOf(atoms, symbols).forEach((c) => take(pool, c)));
  // réponse arrondie (moteur v10) : la valeur arrondie est un bout de la feuille
  if (model.rounding) {
    const r = model.rounding;
    const g = r.symbol ?? model.goal[0]!;
    (symbols[g]?.values ?? []).forEach((v) =>
      take(pool, chip(lex(formatNumber(roundTo(v, r.places))))),
    );
  }
  pool.sort(byValue);

  // ── opérations utiles ──
  const used = new Set<string>(['+', '−', '×', '=']);
  if (model.rounding) used.add('≈');
  lines.forEach((atoms) =>
    atoms.forEach((a) => {
      if ((OP_ORDER as readonly string[]).includes(a)) used.add(a);
    }),
  );
  // une fonction s'écrit avec ses parenthèses : « ln(5) »
  if ([...used].some((o) => FUNC_OPS.has(o))) used.add('(');
  // inéquation (moteur v18) : les quatre sens, pour que la palette ne souffle pas
  // si le sens change
  if (model.kernel && INEQ_OPS.some((o) => used.has(o))) INEQ_OPS.forEach((o) => used.add(o));
  // second degré (moteur v23) : « ou » pour le produit nul et les ensembles de solutions
  if (usesKernelPoly(model)) used.add('ou');
  if (used.has('(') || used.has(')')) {
    used.add('(');
    used.add(')');
  }
  const ops: string[] = OP_ORDER.filter((o) => used.has(o));
  // moteur v41 : un exposant écrit dans la feuille (« x³ », « (2x)³ ») est un bout, juste
  // après « ² » — comme dans une feuille de dérivées
  if (model.kernel) {
    const k = model.kernel;
    const sups = new Set<string>();
    [k.start, k.answer, ...(k.path ?? [])].forEach((t) => {
      try {
        lex(t).forEach((a) => {
          if (isSupPow(a)) sups.add(a);
        });
      } catch {
        /* ligne illisible : rien */
      }
    });
    if (sups.size) {
      const sorted = [...sups].sort((a, b) => supPowValue(a) - supPowValue(b));
      const at = ops.indexOf('²');
      const before = at >= 0 ? at + 1 : ops.indexOf('^') >= 0 ? ops.indexOf('^') : ops.indexOf('=');
      ops.splice(before < 0 ? ops.length : before, 0, ...sorted);
    }
  }
  if (cong) {
    // « ≡ » puis le modulo de la question, juste avant « = »
    const eq = ops.indexOf('=');
    ops.splice(eq < 0 ? ops.length : eq, 0, ...(ops.includes('≡') ? [] : ['≡']));
    ops.splice(ops.indexOf('≡') + 1, 0, `[${cong.mod}]`);
  }

  return { statement, ...(tools.length ? { tools } : {}), answers, values: pool, ops };
}

/**
 * Feuille à formules du noyau (moteur v32, D6 du lot 6) : les grandeurs et les nombres
 * de l'énoncé (valeurs données, égalités affichées, formules de la carte), puis les
 * valeurs du CORRIGÉ (jamais celles des pièges ni des choix du QCM : peu de bouts, et la
 * réponse en fait partie) ; « ≈ » toujours proposé.
 */
function formulaPalette(model: ScratchModel): Palette {
  const parts = kforPaletteParts(model);
  const symbols = model.symbols;
  const taken = new Set<string>();
  const take = (list: PaletteChip[], c: PaletteChip) => {
    const k = chipKey(c.atoms);
    if (!c.atoms.length || taken.has(k)) return;
    taken.add(k);
    list.push(c);
  };
  const read = (t: string): string[] => {
    try {
      return kforLineAtoms(t);
    } catch {
      return [];
    }
  };
  const statement: PaletteChip[] = [];
  const tools: PaletteChip[] = [];
  parts.names.forEach((n) => take(statement, chip([n])));
  parts.tools.forEach((n) => take(tools, chip([n])));
  parts.givenValues.forEach((v) => take(statement, chip(read(v))));
  const statementAtoms = parts.statementTexts.map(read);
  statementAtoms.forEach((atoms) => termsOf(atoms, symbols).forEach((c) => take(statement, c)));
  const pathAtoms = parts.pathTexts.map(read);
  const values: PaletteChip[] = [];
  pathAtoms.forEach((atoms) => termsOf(atoms, symbols).forEach((c) => take(values, c)));
  parts.roundedValues.forEach((v) => take(values, chip(read(v))));
  values.sort(byValue);
  const used = new Set<string>(['+', '−', '×', '=', '≈']);
  [...statementAtoms, ...pathAtoms].forEach((atoms) =>
    atoms.forEach((a) => {
      if ((OP_ORDER as readonly string[]).includes(a)) used.add(a);
    }),
  );
  if ([...used].some((o) => FUNC_OPS.has(o))) used.add('(');
  if (used.has('(') || used.has(')')) {
    used.add('(');
    used.add(')');
  }
  const ops: string[] = OP_ORDER.filter((o) => used.has(o));
  return { statement, ...(tools.length ? { tools } : {}), answers: [], values, ops };
}

/** ordre des opérations d'une trace Python (« = » en dernier) */
export const PYTHON_OPS = [
  '<',
  '>',
  '≤',
  '≥',
  '≠',
  '+',
  '−',
  '×',
  '÷',
  '%',
  '(',
  ')',
  // moteur v52 : une liste « [1, 3, 5] »
  '[',
  ']',
  ',',
  '≈',
  ';',
  '=',
];
/** bouts de CODE (moteur v29, compléter une ligne), dans l'ordre d'affichage */
export const PYTHON_CODE_OPS = [
  '(',
  ')',
  ':',
  ',',
  '[',
  ']',
  '.',
  '+',
  '-',
  '*',
  '/',
  '//',
  '%',
  '**',
  '<',
  '>',
  '<=',
  '>=',
  '==',
  '!=',
  '+=',
  '-=',
  '*=',
  '=',
];
const PY_NUM = /^−?\d+(,\d+)?$/;
/**
 * Trace Python (moteur v28) : les variables suivies et les appels (« pgcd(84, 36) »),
 * puis les nombres et booléens de la trace et des pièges (triés), les opérations utiles
 * (« ; » pour écrire plusieurs variables, « ≈ », les comparaisons).
 */
function pythonPalette(model: ScratchModel): Palette {
  const spec = model.kernel!;
  if (spec.goal.kind === 'complete') return pythonCodePalette(model);
  const parts = kprogPaletteParts(model);
  const statement: PaletteChip[] = [
    ...parts.names.map((n) => ({ atoms: [n], text: n, kind: 'var' as const })),
    ...parts.calls.map((c) => ({ atoms: [c], text: c, kind: 'expr' as const })),
  ];
  const texts = [
    spec.answer,
    ...(spec.path ?? []),
    ...(model.traps ?? []).map((t) => t.line),
    ...(model.choices ?? []).flatMap((c) => (c.line ? [c.line] : [])),
  ];
  const atoms = texts.flatMap((t) => {
    try {
      return kprogLineAtoms(t);
    } catch {
      return [];
    }
  });
  const seen = new Set<string>();
  const values: PaletteChip[] = [];
  atoms.forEach((a) => {
    if (seen.has(a) || !(PY_NUM.test(a) || a === 'True' || a === 'False')) return;
    seen.add(a);
    values.push({ atoms: [a], text: a, kind: PY_NUM.test(a) ? 'num' : 'word' });
  });
  const val = (c: PaletteChip) =>
    PY_NUM.test(c.text) ? Number(c.text.replace('−', '-').replace(',', '.')) : Infinity;
  values.sort((a, b) => val(a) - val(b) || a.text.localeCompare(b.text));
  const used = new Set(['=', ...atoms]);
  if (parts.names.length > 1) used.add(';');
  const ops = PYTHON_OPS.filter((o) => used.has(o));
  return { statement, answers: [], values, ops };
}

/**
 * Compléter une ligne de code (moteur v29) : les mots de Python (for, while, range…), les
 * noms du programme, les nombres, puis les signes de code utiles.
 */
function pythonCodePalette(model: ScratchModel): Palette {
  const parts = kprogCodePaletteParts(model);
  const statement: PaletteChip[] = [
    ...parts.words.map((w) => ({ atoms: [w], text: w, kind: 'word' as const })),
    ...parts.names.map((n) => ({ atoms: [n], text: n, kind: 'var' as const })),
  ];
  const values: PaletteChip[] = parts.numbers.map((n) => ({
    atoms: [n],
    text: n,
    kind: 'num' as const,
  }));
  const ops = [
    ...PYTHON_CODE_OPS.filter((o) => parts.ops.includes(o)),
    ...parts.ops.filter((o) => !PYTHON_CODE_OPS.includes(o)),
  ];
  return { statement, answers: [], values, ops };
}

/**
 * Calcul matriciel (moteur v30) : les matrices (A, B, π₀…), « det », les nombres du calcul
 * (triés), puis les signes : crochets, « , » entre deux coefficients, « ; » entre deux
 * lignes, les opérations et les exposants utiles (², ⁻¹, ᵀ…).
 */
function matrixPalette(model: ScratchModel): Palette {
  const parts = kmatPaletteParts(model);
  const statement: PaletteChip[] = [
    ...parts.names.map((n) => ({ atoms: [n], text: n, kind: 'var' as const })),
    // moteur v52 : un coefficient « (M²)₁₃ » forme un seul bout
    ...parts.entries.map((e) => ({ atoms: kmatLineAtoms(e), text: e, kind: 'var' as const })),
    ...parts.words.map((w) => ({ atoms: [w], text: w, kind: 'word' as const })),
  ];
  const values: PaletteChip[] = parts.numbers.map((n) => ({
    atoms: [n],
    text: n,
    kind: 'num' as const,
  }));
  return { statement, answers: [], values, ops: parts.ops };
}

/**
 * Arithmétique (moteur v35) : les PGCD écrits (un bout chacun), les inconnues de Bézout,
 * les nombres de l'énoncé et du corrigé (triés), les opérations utiles (D6 : jamais les
 * valeurs des pièges ni des choix).
 */
/**
 * Limites (moteur v54) : la variable, l'expression de l'énoncé (un bout), les nombres, les
 * limites (« +∞ », « −∞ », « 0⁺ »…), les opérations et « → » (D6 : jamais les pièges ni
 * les choix).
 */
function limitPalette(model: ScratchModel): Palette {
  const parts = klimPaletteParts(model);
  const startAtoms = klimLineAtoms(model.kernel!.start, { raw: true });
  const statement: PaletteChip[] = [
    ...parts.names.map((n) => ({ atoms: [n], text: n, kind: 'var' as const })),
    ...(startAtoms.length > 1
      ? [{ atoms: startAtoms, text: model.kernel!.start.trim(), kind: 'expr' as const }]
      : []),
  ];
  const values: PaletteChip[] = [
    ...parts.numbers.map((n) => ({ atoms: [n], text: n, kind: 'num' as const })),
    ...parts.limits.map((l) => ({ atoms: [l], text: l, kind: 'num' as const })),
    // v58 : les restes « o(1) », « o(x²) » du corrigé
    ...parts.dl.map((o) => ({ atoms: [o], text: o, kind: 'var' as const })),
  ];
  return { statement, answers: [], values, ops: parts.ops };
}

/**
 * Limites affirmées (moteur v57) : les variables, chaque expression de l'énoncé (un bout),
 * les nombres, les limites (« +∞ », « −∞ », « 0⁺ »…), les opérations et « → » (D6 : jamais
 * les pièges ni les choix).
 */
function limitClaimsPalette(model: ScratchModel): Palette {
  const parts = klcPaletteParts(model);
  const statement: PaletteChip[] = [
    ...parts.names.map((n) => ({ atoms: [n], text: n, kind: 'var' as const })),
    ...parts.exprs.flatMap((e) => {
      const atoms = klimLineAtoms(e, { raw: true });
      return atoms.length > 1 ? [{ atoms, text: e.trim(), kind: 'expr' as const }] : [];
    }),
  ];
  const values: PaletteChip[] = [
    ...parts.numbers.map((n) => ({ atoms: [n], text: n, kind: 'num' as const })),
    ...parts.limits.map((l) => ({ atoms: [l], text: l, kind: 'num' as const })),
  ];
  return { statement, answers: [], values, ops: parts.ops };
}

/**
 * Intégrales (moteur v55) : I, x, u(x)… ; les signes « ∫(a ; b) », « dx », « [ »,
 * « ](a ; b) » ; les nombres de l'énoncé et du corrigé (triés) ; les opérations utiles
 * (D6 : jamais les pièges ni les choix).
 */
function integralPalette(model: ScratchModel): Palette {
  const parts = kintPaletteParts(model);
  const statement: PaletteChip[] = [
    ...parts.names.map((n) => ({ atoms: [n], text: n, kind: 'var' as const })),
    ...parts.signs.map((s) => ({ atoms: [s], text: s, kind: 'op' as const })),
  ];
  const values: PaletteChip[] = parts.numbers.map((n) => ({
    atoms: [n],
    text: n,
    kind: 'num' as const,
  }));
  return { statement, answers: [], values, ops: parts.ops };
}

/**
 * Équations différentielles (moteur v62) : y, y′, y″, x, les constantes, y(0)…, r, Δ, i ;
 * les nombres de l'énoncé et du corrigé (triés) ; les opérations utiles (D6 : jamais les
 * pièges ni les choix).
 */
function odePalette(model: ScratchModel): Palette {
  const parts = kodePaletteParts(model);
  const statement: PaletteChip[] = parts.names.map((n) => ({
    atoms: [n],
    text: n,
    kind: 'var' as const,
  }));
  const values: PaletteChip[] = parts.numbers.map((n) => ({
    atoms: [n],
    text: n,
    kind: 'num' as const,
  }));
  return { statement, answers: [], values, ops: parts.ops };
}

/**
 * Dérivées partielles (moteur v64) : les dérivées de l'énoncé, du corrigé et de la question,
 * les variables (et t), les nombres de l'énoncé et du corrigé (triés), les opérations utiles
 * (« , » pour un couple ; D6 : jamais les pièges ni les choix).
 */
function partialPalette(model: ScratchModel): Palette {
  const parts = kpartPaletteParts(model);
  const statement: PaletteChip[] = parts.names.map((n) => ({
    atoms: [n],
    text: n,
    kind: 'var' as const,
  }));
  const values: PaletteChip[] = parts.numbers.map((n) => ({
    atoms: [n],
    text: n,
    kind: 'num' as const,
  }));
  return { statement, answers: [], values, ops: parts.ops };
}

/**
 * Sommes de Riemann (moteur v59) : la suite, n, l'indice, x, u(x)… ; les signes
 * « Σ(k = 1 ; n) », « ∫(a ; b) », « dx », « [ », « ](a ; b) » ; les nombres de l'énoncé et
 * du corrigé (triés) ; les opérations utiles et « → » (D6 : jamais les pièges ni les choix).
 */
function riemannPalette(model: ScratchModel): Palette {
  const parts = kriemPaletteParts(model);
  const statement: PaletteChip[] = [
    ...parts.names.map((n) => ({ atoms: [n], text: n, kind: 'var' as const })),
    ...parts.signs.map((s) => ({ atoms: [s], text: s, kind: 'op' as const })),
  ];
  const values: PaletteChip[] = parts.numbers.map((n) => ({
    atoms: [n],
    text: n,
    kind: 'num' as const,
  }));
  return { statement, answers: [], values, ops: parts.ops };
}

function arithPalette(model: ScratchModel): Palette {
  const parts = karithPaletteParts(model);
  const statement: PaletteChip[] = [
    ...parts.gcds.map((g) => ({ atoms: [g], text: g, kind: 'var' as const })),
    ...parts.names.map((n) => ({ atoms: [n], text: n, kind: 'var' as const })),
  ];
  const values: PaletteChip[] = parts.numbers.map((n) => ({
    atoms: [n],
    text: n,
    kind: 'num' as const,
  }));
  return { statement, answers: [], values, ops: parts.ops };
}

/**
 * Affirmations à trancher (moteur v36) : chaque membre à calculer d'une affirmation est un
 * bout (« 1 − (−3) », « 5 ÷ 0,4 », « 2x + 1 »), puis les nombres de l'énoncé et du corrigé
 * (triés), les opérations utiles.
 */
function claimsPalette(model: ScratchModel): Palette {
  const parts = kclaimsPaletteParts(model);
  const taken = new Set<string>();
  const take = (list: PaletteChip[], c: PaletteChip) => {
    const k = chipKey(c.atoms);
    if (!c.atoms.length || taken.has(k)) return;
    taken.add(k);
    list.push(c);
  };
  const statement: PaletteChip[] = [];
  parts.sides.forEach((a) => take(statement, chip(a)));
  // moteur v50 : les lettres d'une identité, pour calculer avec elles
  parts.letters.forEach((l) => take(statement, { atoms: [l], text: l, kind: 'var' }));
  const values: PaletteChip[] = [];
  parts.numbers.forEach((n) => take(values, chip([n])));
  parts.atoms.filter((a) => isPow(a)).forEach((a) => take(values, chip([a])));
  values.sort(byValue);
  const used = new Set<string>(['+', '−', '×', '÷', '(', ')', '=']);
  parts.atoms.forEach((a) => {
    if ((OP_ORDER as readonly string[]).includes(a) && !INEQ_OPS.includes(a as never)) used.add(a);
  });
  if ([...used].some((o) => FUNC_OPS.has(o))) used.add('(');
  const ops: string[] = OP_ORDER.filter((o) => used.has(o));
  return { statement, answers: [], values, ops };
}

/**
 * Dérivées et primitives (moteur v31) : les noms de fonctions (f(x), f′(x), u(x), u′(x)…),
 * la variable et la constante C, puis les nombres (triés) et les opérations utiles
 * (exposants, √, exp, ln, cos, sin…).
 */
function analysisPalette(model: ScratchModel): Palette {
  const parts = kanaPaletteParts(model);
  const statement: PaletteChip[] = parts.names.map((n) => ({
    atoms: [n],
    text: n,
    kind: 'var' as const,
  }));
  const values: PaletteChip[] = parts.numbers.map((n) => ({
    atoms: [n],
    text: n,
    kind: 'num' as const,
  }));
  return { statement, answers: [], values, ops: parts.ops };
}

/**
 * Étude de fonction (lot 6.20) : les bouts de la dérivée quand il faut dériver f (ceux de
 * la feuille d'analyse) ; rien quand l'énoncé donne f′ — le tableau se dresse dans son
 * éditeur (bouton de la palette).
 */
function variationsPalette(model: ScratchModel): Palette {
  const amodel = analysisModelOf(model);
  if (amodel) return analysisPalette(amodel);
  return { statement: [], answers: [], values: [], ops: [] };
}

/**
 * Tableau à remplir (lot 6.21) : les grandeurs à lire (« Me », « Q₁ », « k »), les valeurs
 * lues du corrigé (et celles de la série), « = » ; le tableau se remplit dans son éditeur.
 */
function tablesPalette(model: ScratchModel): Palette {
  const parts = ktabPaletteParts(model);
  return {
    statement: parts.names.map((n) => ({ atoms: [n], text: n, kind: 'var' as const })),
    answers: [],
    values: parts.numbers.map((n) => ({ atoms: [n], text: n, kind: 'num' as const })),
    ops: parts.names.length ? ['='] : [],
  };
}

/**
 * Grandeurs et unités (moteur v4) : les nombres et les unités de la question,
 * puis ceux du corrigé et des pièges (triés), et les opérations utiles.
 */
function unitsPalette(model: ScratchModel): Palette {
  const spec = model.units!;
  const taken = new Set<string>();
  const take = (list: PaletteChip[], atoms: string[], text?: string) => {
    const k = atoms.join(' ');
    if (taken.has(k)) return;
    taken.add(k);
    list.push({ atoms, text: text ?? atomsText(atoms), kind: chipKind(atoms) });
  };
  const num = (list: PaletteChip[], n: string) => take(list, [n]);
  const unit = (list: PaletteChip[], u: string) => take(list, [unitAtom(u)], u);

  const statement: PaletteChip[] = [];
  const statementTexts = [spec.target, ...(model.givens ?? [])];
  statementTexts.forEach((t) => numbersIn(t).forEach((n) => num(statement, n)));
  const others = [...spec.path, ...(model.traps ?? []).map((t) => t.line)];
  // toutes les unités en jeu, dans l'ordre où elles apparaissent (question, réponse, corrigé, pièges)
  [...statementTexts, spec.unit.replace(/\s+/g, ' '), spec.valueUnit ?? '', ...others]
    .flatMap((t) => unitsIn(t))
    .forEach((u) => unit(statement, u));

  const answers: PaletteChip[] = [];
  (model.choices ?? []).forEach((c) => {
    if (c.value !== undefined) num(answers, formatNumber(c.value));
  });
  answers.sort(byValue);

  const values: PaletteChip[] = [];
  others.forEach((t) => numbersIn(t).forEach((n) => num(values, n)));
  values.sort(byValue);

  const used = new Set<string>(['=']);
  [...statementTexts, ...others].forEach((t) => {
    if (/\+/.test(t)) used.add('+');
    if (/[−-]/.test(t)) used.add('−');
    if (/×/.test(t)) used.add('×');
    if (/÷/.test(t)) used.add('÷');
    if (/[()]/.test(t)) {
      used.add('(');
      used.add(')');
    }
  });
  return { statement, answers, values, ops: OP_ORDER.filter((o) => used.has(o)) };
}

/**
 * Encadrer une racine carrée (moteur v13) : la racine et le nombre sous la racine,
 * puis les nombres du corrigé MÊLÉS à leurs voisins (bornes et carrés voisins :
 * la palette ne souffle pas l'encadrement), triés ; opérations ² √ × < > =.
 */
function boundsPalette(model: ScratchModel): Palette {
  const spec = model.bounds!;
  const exprAtoms = normalize(lex(spec.expr));
  const inner = exprAtoms[0] === '√' && exprAtoms.length === 2 ? exprAtoms[1]! : null;
  const statement: PaletteChip[] = [
    { atoms: exprAtoms, text: atomsText(exprAtoms), kind: 'expr' },
    ...(inner ? [{ atoms: [inner], text: inner, kind: 'num' as const }] : []),
  ];
  const parseN = (t: string) => Number(t.replace(',', '.'));
  const fmt = (v: number) => formatNumber(Math.round(v * 1e6) / 1e6);
  const nums = new Set<string>();
  // une seule borne (moteur v14) : elle est dans la question, on la donne
  const one = spec.lower === undefined || spec.upper === undefined;
  if (one) {
    const b = normalize(lex((spec.lower ?? spec.upper)!));
    statement.push({ atoms: b, text: atomsText(b), kind: b.length === 1 ? 'num' : 'expr' });
  }
  const lo = spec.lower === undefined ? NaN : parseN(spec.lower);
  const hi = spec.upper === undefined ? NaN : parseN(spec.upper);
  const dec = Math.max(
    ...[spec.lower ?? '', spec.upper ?? ''].map((t) =>
      t.includes(',') ? t.split(',')[1]!.length : 0,
    ),
  );
  const unit = 10 ** -dec;
  [...spec.path, ...(model.traps ?? []).map((t) => t.line)]
    .flatMap((t) => normalize(lex(t)))
    .filter((a) => isNum(a))
    .forEach((a) => nums.add(a));
  [lo - unit, hi + unit].forEach((v) => {
    if (!one && v > 0) {
      nums.add(fmt(v));
      nums.add(fmt(v * v));
    }
  });
  if (inner) nums.delete(inner);
  const values = [...nums]
    .sort((a, b) => parseN(a) - parseN(b))
    .map((a) => ({ atoms: [a], text: a, kind: 'num' as const }));
  const used = new Set(['²', '√', '<', '=', ...[...spec.path].flatMap((t) => normalize(lex(t)))]);
  return { statement, answers: [], values, ops: OP_ORDER.filter((o) => used.has(o)) };
}

/**
 * Raisonnement géométrique (moteur v4) : les droites, segments et points de
 * l'énoncé et du corrigé (triés), puis les relations (« // », « ⊥ »,
 * « milieu de »…) à la place des opérations.
 */
function geometryPalette(model: ScratchModel): Palette {
  const spec = model.geometry!;
  const taken = new Set<string>();
  const objects = (texts: string[]): PaletteChip[] => {
    const out: PaletteChip[] = [];
    texts
      .flatMap((t) => geoChipsOf(t))
      .filter((a) => !GEO_RELATIONS.includes(a))
      .forEach((a) => {
        if (taken.has(a)) return;
        taken.add(a);
        out.push({ atoms: [a], text: a, kind: 'var' });
      });
    return out;
  };
  const statement = objects([...(model.givens ?? []), spec.goal]);
  const values = objects([...spec.path, ...(model.traps ?? []).map((t) => t.line)]).sort((a, b) =>
    a.text.localeCompare(b.text, 'fr'),
  );
  const used = new Set(
    [
      ...(model.givens ?? []),
      spec.goal,
      ...spec.path,
      ...(model.traps ?? []).map((t) => t.line),
    ].flatMap((t) => geoChipsOf(t).filter((a) => GEO_RELATIONS.includes(a))),
  );
  return { statement, answers: [], values, ops: GEO_RELATIONS.filter((r) => used.has(r)) };
}

const isGeoRel = (a: string): boolean => (KGEO_RELATIONS as readonly string[]).includes(a);
/**
 * Les bouts d'une ligne de géométrie (moteur v4 et v25) : « I milieu de [AB] » →
 * [I] [milieu de] [[AB]] ; une relation entre longueurs se découpe comme d'habitude
 * (« BC² ≠ AB² + AC² »), « ≠ » compris.
 */
export function geometryLineChips(text: string): PaletteChip[] {
  if (looksGeometric(text))
    return gpropChips(text).map((a) => ({
      atoms: [a],
      text: a,
      kind: isGeoRel(a) ? 'word' : 'var',
    }));
  return text.split(/\s*(=|≠)\s*/).flatMap((p, i) => (i % 2 ? [chip([p])] : groupAtoms(lex(p))));
}
/** Les bouts d'une ligne de l'énoncé (affichage en tête de feuille, recopie). */
export function sheetLineChips(model: ScratchModel, text: string): PaletteChip[] {
  if (model.geometry || model.kernel?.plugin === 'geometry') return geometryLineChips(text);
  if (model.kernel?.plugin === 'matrix') return matrixLineChips(text);
  // arithmétique (moteur v35) : « PGCD(1071 ; 462) » forme un seul bout
  if (model.kernel?.plugin === 'arith') return groupAtoms(karithLineAtoms(text));
  // limites (moteur v54) : « x → +∞ », la limite d'un bloc
  if (model.kernel?.plugin === 'limit' || model.kernel?.plugin === 'limclaims')
    return groupAtoms(klimLineAtoms(text, { raw: true }));
  // intégrales (moteur v55) : « ∫(0 ; 1) », « dx », « ](0 ; 1) » restent d'un bloc
  if (model.kernel?.plugin === 'integral') return groupAtoms(kintLineAtoms(text));
  // équations différentielles (moteur v62) : « y′ », « y″ », « y(0) » restent d'un bloc
  if (model.kernel?.plugin === 'ode')
    return groupAtoms(kodeLineAtoms(text, model.kernel.goal.target, odeVariable(model)));
  if (model.kernel?.plugin === 'partial')
    return groupAtoms(kpartLineAtoms(text, partialSpecOf(model)));
  // sommes de Riemann (moteur v59) : « Σ(k = 1 ; n) » reste d'un bloc
  if (model.kernel?.plugin === 'riemann') return groupAtoms(kriemLineAtoms(text));
  // tableau à remplir (lot 6.21) : le tableau de l'énoncé est un seul bout
  if (model.kernel?.plugin === 'tables' && isGridText(text))
    return [{ atoms: [text], text: 'Tableau', kind: 'var' }];
  // dérivées (moteur v31) : « x³ », « x¹⁰ » gardent leurs exposants d'un bloc
  if (model.kernel?.plugin === 'analysis' || model.kernel?.plugin === 'variations')
    return groupAtoms(kanaLineAtoms(text));
  return chipsFromText(text);
}

/**
 * Calcul matriciel (moteur v30) : une ligne de l'énoncé en bouts ; une matrice écrite
 * (« [1, 2 ; 3, 4] ») forme un seul bout, qui se recopie d'un coup.
 */
export function matrixLineChips(text: string): PaletteChip[] {
  const atoms = kmatLineAtoms(text);
  const out: PaletteChip[] = [];
  for (let i = 0; i < atoms.length; i++) {
    if (atoms[i] === '[') {
      let depth = 0;
      let j = i;
      for (; j < atoms.length; j++) {
        if (atoms[j] === '[' || atoms[j] === '(') depth++;
        if (atoms[j] === ']' || atoms[j] === ')') depth--;
        if (depth === 0) break;
      }
      if (j < atoms.length && atoms[j] === ']') {
        const m = atoms.slice(i, j + 1);
        out.push({ atoms: m, text: matText(m), kind: chipKind(m) });
        i = j;
        continue;
      }
    }
    // moteur v52 : un coefficient « (M²)₁₃ » forme un seul bout
    if (atoms[i] === '(') {
      let depth = 0;
      let j = i;
      for (; j < atoms.length; j++) {
        if (atoms[j] === '(') depth++;
        if (atoms[j] === ')') depth--;
        if (depth === 0) break;
      }
      if (j + 1 < atoms.length && isEntryAtom(atoms[j + 1]!)) {
        const m = atoms.slice(i, j + 2);
        out.push({ atoms: m, text: matText(m), kind: 'var' });
        i = j + 1;
        continue;
      }
    }
    const a = atoms[i]!;
    out.push({ atoms: [a], text: a, kind: chipKind([a]) });
  }
  return out;
}

/**
 * Démonstration de géométrie (moteur v25) : les points, droites, segments, figures et
 * longueurs de l'énoncé, puis ceux du corrigé et des pièges (triés), les nombres ; les
 * relations (« // », « milieu de »…) et les opérations utiles.
 */
function kernelGeometryPalette(model: ScratchModel): Palette {
  const spec = model.kernel!;
  const taken = new Set<string>();
  const pieces = (texts: string[]): PaletteChip[] => {
    const out: PaletteChip[] = [];
    texts
      .flatMap((t) => geometryLineChips(t))
      .forEach((c) => {
        const a = c.atoms.join(' ');
        const isNumChip = c.atoms.length === 1 && isNum(c.atoms[0]);
        const isObject = c.kind === 'var' && !isGeoRel(a);
        const isLength =
          c.atoms.length <= 2 &&
          /^[A-Z]{2}$/.test(c.atoms[0] ?? '') &&
          (c.atoms.length === 1 || c.atoms[1] === '²');
        if (!(isNumChip || (looksGeometricObject(a) && isObject) || isLength)) return;
        // une longueur au carré : le bout est la longueur (le carré vient de l'opération « ² »)
        const atoms = isLength ? [c.atoms[0]!] : c.atoms;
        // « AB » et « BA » : une seule longueur, un seul bout (le premier écrit)
        const k = isLength ? [...atoms[0]!].sort().join('') : atoms.join(' ');
        if (taken.has(k)) return;
        taken.add(k);
        out.push({ atoms, text: atoms[0]!, kind: isNumChip ? 'num' : 'var' });
      });
    return out;
  };
  const statementTexts = [spec.start, ...(model.givens ?? [])];
  const otherTexts = [
    spec.answer,
    ...(spec.path ?? []),
    ...(model.traps ?? []).map((t) => t.line),
    ...(model.choices ?? []).flatMap((c) => (c.line ? [c.line] : [])),
  ];
  const statement = pieces(statementTexts);
  const values = pieces(otherTexts).sort(byValue);
  const used = new Set(
    [...statementTexts, ...otherTexts].flatMap((t) => geometryLineChips(t).flatMap((c) => c.atoms)),
  );
  const rels = (KGEO_RELATIONS as readonly string[]).filter((r) => used.has(r));
  const arith = OP_ORDER.filter((o) => used.has(o) && o !== '=');
  if (arith.includes('(') || arith.includes(')')) {
    if (!arith.includes('(')) arith.splice(arith.indexOf(')'), 0, '(');
    if (!arith.includes(')')) arith.splice(arith.indexOf('(') + 1, 0, ')');
  }
  const ops = [...rels, ...arith, ...(used.has('≠') ? ['≠'] : []), ...(used.has('=') ? ['='] : [])];
  return { statement, answers: [], values, ops };
}
/** un point, une droite, un segment ou une figure (« ABCD ») */
function looksGeometricObject(a: string): boolean {
  return /^[A-Z]{1,4}$/.test(a) || /^\(.+\)$/.test(a) || /^\[.+\]$/.test(a);
}

/** Notations de la feuille : le sens de chaque lettre qui en a un (`symbols[n].meaning`). */
export function notationsOf(model: ScratchModel): { name: string; meaning: string }[] {
  return Object.entries(model.symbols ?? {}).flatMap(([name, s]) =>
    s.meaning?.trim() ? [{ name, meaning: s.meaning.trim() }] : [],
  );
}

/** Tous les bouts (hors opérations), dans l'ordre d'affichage. */
export function paletteChips(p: Palette): PaletteChip[] {
  return [...p.statement, ...(p.tools ?? []), ...p.answers, ...p.values];
}

/** les atomes écrivent-ils un complexe a + bi ? (bout « (1 + i) » de la palette) */
function algebraicAtoms(atoms: string[]): boolean {
  try {
    return isAlgebraic(parseExpr(normalize(atoms)));
  } catch {
    return false;
  }
}
